import { useMemo } from "react";
import { Task, TaskFilters } from "../types/task";

/**
 * Custom hook to filter tasks based on search and tags
 */
export const useTaskFilters = (tasks: Task[] | null, filters: TaskFilters) => {
  return useMemo(() => {
    if (!tasks) return [];

    // Filter tasks
    let filteredTasks = tasks;
    if (filters.search || filters.tags.length) {
      filteredTasks = tasks.filter((task) => {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = !filters.search ||
          task.task.toLowerCase().includes(searchLower) ||
          (task.assignee_name && task.assignee_name.toLowerCase().includes(searchLower)) ||
          (task.owner_email && task.owner_email.toLowerCase().includes(searchLower));

        const matchesTags = !filters.tags.length ||
          filters.tags.every((tag) => task.tags?.includes(tag));

        return matchesSearch && matchesTags;
      });
    }

    // Sort tasks based on sortBy option
    if (filters.sortBy === 'deadline') {
      return [...filteredTasks].sort((a, b) => {
        // Nulls go to the end
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;

        // Sort by deadline ascending (earliest first)
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      });
    }

    if (filters.sortBy === 'priority') {
      return [...filteredTasks].sort((a, b) => {
        // Calculate priority: urgent + important = high priority
        const priorityA = a.urgency + a.importance;
        const priorityB = b.urgency + b.importance;

        // Sort descending (highest priority first)
        return priorityB - priorityA;
      });
    }

    // Default: return in original order (created_at)
    return filteredTasks;
  }, [tasks, filters]);
};
